import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ChevronDown, Check, Layers } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact season chip rendered in the sidebar header. Click to open a
 * dropdown that lets the coach jump between seasons or deselect entirely
 * (falls back to account defaults).
 *
 * Hidden for non-admins and for accounts that haven't created a season yet.
 */
export function SeasonSwitcher() {
  const { user, isAdmin, fetchUser } = useAuth();
  const [seasons, setSeasons] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/seasons`);
        if (!cancelled) setSeasons(resp.data || []);
      } catch {
        // silent — switcher just won't render
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, user?.current_season_id]);

  if (!isAdmin || seasons.length === 0) return null;

  const activeSeason = seasons.find((s) => s.id === user?.current_season_id);
  const label = activeSeason ? activeSeason.name : 'No season';

  const switchTo = async (seasonId) => {
    if (busy) return;
    if (seasonId === user?.current_season_id) { setOpen(false); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/settings/current-season/${seasonId}`);
      await fetchUser();
      const next = seasons.find((s) => s.id === seasonId);
      toast.success(`Switched to ${next?.name || 'season'}`);
    } catch {
      toast.error('Failed to switch season');
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const deselect = async () => {
    if (busy || !user?.current_season_id) { setOpen(false); return; }
    setBusy(true);
    try {
      await axios.delete(`${API}/settings/current-season`);
      await fetchUser();
      toast.success('Using account defaults');
    } catch {
      toast.error('Failed to deselect season');
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 max-w-full px-2 py-0.5 rounded-full bg-white/10 hover:bg-white/15 transition-colors text-xs text-white/85 disabled:opacity-50"
          disabled={busy}
          data-testid="season-switcher-trigger"
        >
          <Layers className="w-3 h-3 shrink-0" strokeWidth={2} />
          <span className="truncate max-w-[150px]">{label}</span>
          <ChevronDown className="w-3 h-3 shrink-0 opacity-70" strokeWidth={2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60" data-testid="season-switcher-menu">
        <DropdownMenuLabel className="text-xs text-gray-500 font-normal">Switch active season</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {seasons.map((s) => {
          const isActive = s.id === user?.current_season_id;
          return (
            <DropdownMenuItem
              key={s.id}
              onClick={() => switchTo(s.id)}
              className="flex items-center justify-between gap-2 cursor-pointer"
              data-testid={`season-switcher-item-${s.id}`}
            >
              <span className="truncate">{s.name}</span>
              {isActive && <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={deselect}
          disabled={!user?.current_season_id}
          className="cursor-pointer text-gray-600"
          data-testid="season-switcher-deselect"
        >
          No season (account defaults)
          {!user?.current_season_id && <Check className="w-3.5 h-3.5 text-emerald-600 ml-auto" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
